import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ReceiptIndianRupee, Plus, Trash2, Loader2 } from "lucide-react";
import { DEFAULT_FEE_HEADS } from "@/lib/feeHeads";
import { toast } from "sonner";

const FREQ = ["Monthly", "Quarterly", "Half-Yearly", "Yearly", "One-Time"];
const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
function monthsForFreq(freq) {
  switch (freq) {
    case "Monthly": return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    case "Quarterly": return [0, 3, 6, 9];
    case "Half-Yearly": return [0, 6];
    case "Yearly": return [0];
    default: return [0];
  }
}
const SESSION = "2026-27";

export default function FeeStructure() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [feeMode, setFeeMode] = useState("same"); // "same" = Same Class Same Fees | "section" = Different Section Different Fees
  const [classId, setClassName] = useState("");
  const [items, setItems] = useState([{ fee_name: "Tuition Fee", amount: 0, frequency: "Monthly", collection_months: monthsForFreq("Monthly") }]);
  const [copyTick, setCopyTick] = useState(0);
  const [sameFees, setSameFees] = useState(false);
  const [multiClassNames, setMultiClassNames] = useState([]);
  const [saving, setSaving] = useState(false);
  const [feeTypes, setFeeTypes] = useState(DEFAULT_FEE_HEADS);

  const load = () => api.get("/fee-structures").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/classes").then((r) => setClasses(r.data));
    api.get("/settings").then((r) => { const h = r.data?.fee_heads; if (h && h.length) setFeeTypes(h); });
  }, []);

  const classNames = [...new Set(classes.map((c) => (c.name || "").trim()).filter(Boolean))];
  const sectionClasses = classes.filter((c) => (c.name || "").trim()).sort((a, b) => (a.name || "").localeCompare(b.name || "") || (a.section || "").localeCompare(b.section || ""));
  const idsForName = (name) => classes.filter((c) => (c.name || "").trim() === (name || "").trim()).map((c) => c.id);

  // Build display cards: group structures by class name. If all sections of a class share the same
  // fees (or only one exists) show a single card by name; otherwise show one card per section.
  const buildCards = () => {
    if (!list) return [];
    const byName = {};
    list.filter((fs) => (fs.class_name || "").trim()).forEach((fs) => { (byName[fs.class_name] = byName[fs.class_name] || []).push(fs); });
    const cards = [];
    Object.entries(byName).forEach(([name, arr]) => {
      const uniform = arr.every((a) => JSON.stringify(a.items) === JSON.stringify(arr[0].items));
      if (arr.length === 1 || uniform) {
        cards.push({ key: name, title: name, mode: "same", fs: arr[0] });
      } else {
        arr.forEach((a) => cards.push({ key: a.id, title: `${name}${a.section ? ` - ${a.section}` : ""}`, mode: "section", fs: a }));
      }
    });
    return cards;
  };
  const cards = buildCards();
  const sameModeNames = [...new Set(cards.filter((c) => c.mode === "same").map((c) => c.title))];

  const openFor = (card) => {
    setSameFees(false); setMultiClassNames([]);
    if (card) {
      setFeeMode(card.mode);
      setClassName(card.mode === "section" ? card.fs.class_id : card.fs.class_name);
      setItems(card.fs.items);
    } else {
      setFeeMode("same"); setClassName(""); setItems([{ fee_name: "Tuition Fee", amount: 0, frequency: "Monthly", collection_months: monthsForFreq("Monthly") }]);
    }
    setOpen(true);
  };

  const switchMode = (m) => { setFeeMode(m); setClassName(""); setSameFees(false); setMultiClassNames([]); };

  const toggleMulti = (nm) =>
    setMultiClassNames((prev) => (prev.includes(nm) ? prev.filter((x) => x !== nm) : [...prev, nm]));
  const allMultiSelected = classNames.length > 0 && multiClassNames.length === classNames.length;
  const toggleAllMulti = () => setMultiClassNames(allMultiSelected ? [] : classNames);

  const save = async () => {
    const payloadItems = items.map((i) => ({ ...i, amount: Number(i.amount), collection_months: (i.collection_months && i.collection_months.length) ? i.collection_months : monthsForFreq(i.frequency) }));
    if (feeMode === "same" && sameFees) {
      if (!multiClassNames.length) { toast.error("Select at least one class"); return; }
      const ids = multiClassNames.flatMap(idsForName);
      setSaving(true);
      try {
        await Promise.all(ids.map((cid) =>
          api.post("/fee-structures", { class_id: cid, academic_session: SESSION, items: payloadItems })));
        toast.success(`Same fees applied to ${multiClassNames.length} class(es)`);
        setOpen(false); load();
      } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
      finally { setSaving(false); }
      return;
    }
    if (!classId) { toast.error("Select a class"); return; }
    if (feeMode === "section") {
      setSaving(true);
      try {
        await api.post("/fee-structures", { class_id: classId, academic_session: SESSION, items: payloadItems });
        toast.success("Fee structure saved for selected section");
        setOpen(false); load();
      } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
      finally { setSaving(false); }
      return;
    }
    const ids = idsForName(classId);
    if (!ids.length) { toast.error("Class not found"); return; }
    setSaving(true);
    try {
      await Promise.all(ids.map((cid) =>
        api.post("/fee-structures", { class_id: cid, academic_session: SESSION, items: payloadItems })));
      toast.success(ids.length > 1 ? `Fee structure saved for all ${ids.length} sections of ${classId}` : "Fee structure saved");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const copyFrom = async (targetFs, sourceName) => {
    const src = list.find((f) => f.class_name === sourceName);
    if (!src) return;
    const ids = idsForName(targetFs.class_name);
    try {
      await Promise.all(ids.map((cid) => api.post("/fee-structures", {
        class_id: cid, academic_session: SESSION,
        items: src.items.map((i) => ({ fee_name: i.fee_name, amount: Number(i.amount), frequency: i.frequency, applies_to: i.applies_to || "new", collection_months: (i.collection_months && i.collection_months.length) ? i.collection_months : monthsForFreq(i.frequency) })),
      })));
      toast.success(`Copied fees from ${src.class_name} to ${targetFs.class_name}`);
      setCopyTick((t) => t + 1);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!list) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const modeOptions = [
    { id: "same", title: "Same Class Same Fees", desc: "One fee set for all sections of a class" },
    { id: "section", title: "Different Section Different Fees", desc: "Set fees per section (e.g. 1st - A, 1st - B)" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-extrabold text-slate-900">Fee Structure</h1><p className="text-slate-500">Define fees per class</p></div>
        {hasPerm(user, "add_fee_structure") && <Button onClick={() => openFor(null)} data-testid="add-fee-btn"><Plus size={18} className="mr-2" />Set Fee Structure</Button>}
      </div>
      {cards.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400"><ReceiptIndianRupee size={40} className="mx-auto mb-2 opacity-40" />No fee structures yet.</div> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {cards.map((card) => {
            const fs = card.fs;
            return (
              <div key={card.key} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`fee-card-${card.title}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading font-bold text-slate-900">{card.title}</div>
                  <div className="text-right"><div className="text-xs text-slate-400">Annual</div><div className="font-bold text-indigo-600 tabular">{fmt(fs.total_annual)}</div></div>
                </div>
                <div className="space-y-1 text-sm">
                  {fs.items.map((i, idx) => (
                    <div key={idx} className="flex justify-between text-slate-600"><span>{i.fee_name} <span className="text-xs text-slate-400">({i.frequency})</span></span><span className="tabular">{fmt(i.amount)}</span></div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {hasPerm(user, "edit_fee_structure") && <Button size="sm" variant="ghost" onClick={() => openFor(card)} data-testid={`edit-fee-${card.title}`}>Edit</Button>}
                  {hasPerm(user, "edit_fee_structure") && card.mode === "same" && sameModeNames.filter((n) => n !== card.title).length > 0 && (
                    <Select key={`copy-${card.key}-${copyTick}`} onValueChange={(v) => copyFrom(fs, v)}>
                      <SelectTrigger className="h-8 w-44 text-xs" data-testid={`copy-fees-${card.title}`}><SelectValue placeholder="Copy Fees from…" /></SelectTrigger>
                      <SelectContent>{sameModeNames.filter((n) => n !== card.title).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[75vw] max-w-[75vw] sm:max-w-[75vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Fee Structure</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fees Setup</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {modeOptions.map((m) => {
                  const on = feeMode === m.id;
                  return (
                    <button key={m.id} type="button" onClick={() => switchMode(m.id)} data-testid={`fee-mode-${m.id}`}
                      className={`text-left rounded-lg border p-3 transition-colors ${on ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}>
                      <div className={`text-sm font-semibold ${on ? "text-indigo-700" : "text-slate-700"}`}>{m.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{m.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            {!(feeMode === "same" && sameFees) && (
              <div><Label>Class</Label>
                {feeMode === "same" ? (
                  <Select value={classId} onValueChange={setClassName}><SelectTrigger data-testid="fee-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>{classNames.map((nm) => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}</SelectContent></Select>
                ) : (
                  <Select value={classId} onValueChange={setClassName}><SelectTrigger data-testid="fee-class-select"><SelectValue placeholder="Select class & section" /></SelectTrigger>
                    <SelectContent>{sectionClasses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.section ? ` - ${c.section}` : ""}</SelectItem>)}</SelectContent></Select>
                )}
              </div>
            )}
            {feeMode === "same" && (
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="same-fees-checkbox-label">
                <Checkbox checked={sameFees} onCheckedChange={(v) => { setSameFees(!!v); setMultiClassNames([]); }} data-testid="same-fees-checkbox" />
                <span className="text-sm font-medium text-slate-700">Set Same Fees</span>
                <span className="text-xs text-slate-400">(apply these fees to multiple classes)</span>
              </label>
            )}
            {feeMode === "same" && sameFees && (
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Select Classes</Label>
                  <button type="button" onClick={toggleAllMulti} className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="same-fees-select-all">
                    {allMultiSelected ? "Clear All" : "Select All"}
                  </button>
                </div>
                {classNames.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">No classes created yet. Add classes first.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto pr-1">
                    {classNames.map((nm) => {
                      const on = multiClassNames.includes(nm);
                      return (
                        <label key={nm} className={`flex items-center gap-2 rounded-md border px-2 py-1.5 cursor-pointer transition-colors ${on ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`same-fees-class-${nm}`}>
                          <Checkbox checked={on} onCheckedChange={() => toggleMulti(nm)} />
                          <span className="text-xs font-medium text-slate-700 truncate">{nm}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-[11px] text-slate-500 mt-2">{multiClassNames.length} class(es) selected</p>
              </div>
            )}
            <div className="space-y-2">
              {items.map((it, idx) => {
                const cm = (it.collection_months && it.collection_months.length) ? it.collection_months : monthsForFreq(it.frequency);
                return (
                <div key={idx} className="rounded-lg border border-slate-200 p-2.5 space-y-2" data-testid={`fee-item-${idx}`}>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1"><Label className="text-xs">Fee</Label>
                      <Select value={it.fee_name} onValueChange={(v) => setItems(items.map((x, i) => i === idx ? { ...x, fee_name: v } : x))}><SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{feeTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div className="w-24"><Label className="text-xs">Amount</Label><Input type="number" value={it.amount} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} data-testid={`fee-amount-${idx}`} /></div>
                    <div className="w-32"><Label className="text-xs">Frequency</Label>
                      <Select value={it.frequency} onValueChange={(v) => setItems(items.map((x, i) => i === idx ? { ...x, frequency: v, collection_months: monthsForFreq(v) } : x))}><SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{FREQ.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                    </div>
                    {it.fee_name.toLowerCase().includes("admission") && (
                      <div className="w-48"><Label className="text-xs">Admission Fee for</Label>
                        <Select value={it.applies_to || undefined} onValueChange={(v) => setItems(items.map((x, i) => i === idx ? { ...x, applies_to: v } : x))}>
                          <SelectTrigger data-testid={`fee-applies-${idx}`}><SelectValue placeholder="Admission Fee For" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New Students</SelectItem>
                            <SelectItem value="both">New &amp; Old Students</SelectItem>
                            <SelectItem value="discount">Discount this Year</SelectItem>
                          </SelectContent></Select>
                      </div>
                    )}
                    <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 size={16} /></Button>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Collection Month(s)</Label>
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`fee-months-${idx}`}>
                      {MONTHS.map((m, mi) => {
                        const on = cm.includes(mi);
                        return <button type="button" key={mi} data-testid={`fee-month-${idx}-${mi}`} onClick={() => setItems(items.map((x, i) => { if (i !== idx) return x; const base = (x.collection_months && x.collection_months.length) ? x.collection_months : monthsForFreq(x.frequency); const nm = base.includes(mi) ? base.filter((z) => z !== mi) : [...base, mi].sort((a, b) => a - b); return { ...x, collection_months: nm }; }))} className={`text-[11px] rounded px-2 py-1 border transition-colors ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-500 border-slate-200 hover:border-indigo-300"}`}>{m}</button>;
                      })}
                    </div>
                  </div>
                </div>
                );
              })}
              <Button size="sm" variant="outline" onClick={() => setItems([...items, { fee_name: "Other Fee", amount: 0, frequency: "One-Time", collection_months: monthsForFreq("One-Time") }])} data-testid="add-fee-item-btn"><Plus size={14} className="mr-1" />Add Fee Item</Button>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} data-testid="save-fee-btn">{saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}{feeMode === "same" && sameFees ? "Apply to Selected" : "Save Structure"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
